import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Visibility as PreviewIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import ChapterTree from './ChapterTree';
import WritingArea from './WritingArea';
import { apiClient } from '@/services/api';

interface Chapter {
  template_chapter_id: number;
  title: string;
  actual_title?: string;
  children?: Chapter[];
  is_indicator_driven?: boolean;
  content_status?: string;
  level?: number;
}

interface ProjectStructure {
  project: {
    id: number;
    name: string;
  };
  chapters: Chapter[];
}

interface WritingWorkspaceProps {
  projectId: string;
}

const WritingWorkspace: React.FC<WritingWorkspaceProps> = ({ projectId }) => {
  const [projectStructure, setProjectStructure] = useState<ProjectStructure | null>(null);
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const [chapterContent, setChapterContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'info' | 'success' | 'warning' | 'error' });

  // 加载项目结构
  const loadProjectStructure = useCallback(async (autoSelectFirst = true) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/projects/${projectId}/writing/structure`);
      
      if (!response.success) {
        throw new Error(response.message || '获取项目结构失败');
      }
      
      setProjectStructure(response.data);
      
      if (autoSelectFirst) {
        if (response.data.chapters && response.data.chapters.length > 0) {
          const firstSelectableChapter = findFirstSelectableChapter(response.data.chapters);
          if (firstSelectableChapter) {
            await handleChapterSelect(firstSelectableChapter);
          }
        }
      }
    } catch (error: any) {
      setError('加载项目结构失败');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // 递归查找第一个可选择的章节
  const findFirstSelectableChapter = (chapters: Chapter[]): Chapter | null => {
    for (const chapter of chapters) {
      // 如果有子章节，先检查子章节
      if (chapter.children && chapter.children.length > 0) {
        const childResult = findFirstSelectableChapter(chapter.children);
        if (childResult) return childResult;
      }
      // 如果没有子章节或子章节中没有找到，返回当前章节
      if (!chapter.children || chapter.children.length === 0) {
        return chapter;
      }
    }
    return null;
  };

  // 加载章节内容
  const loadChapterContent = async (chapter: Chapter) => {
    try {
      const response = await apiClient.get(`/projects/${projectId}/writing/chapter/${chapter.template_chapter_id}`);
      
      if (!response.success) {
        throw new Error(response.message || '获取章节内容失败');
      }
      
      // 优先使用临时内容，其次使用数据库内容
      const content = response.data.temp_content || response.data.chapter?.content_markdown || '';
      setChapterContent(content);
      
      // 如果有内容，进行文本分析
      if (content.trim()) {
        await analyzeText(content);
      }
    } catch (error: any) {
      showSnackbar('加载章节内容失败', 'error');
    }
  };

  // 文本分析
  const analyzeText = async (text: string) => {
    if (!text || text.trim().length === 0) {
      return;
    }

    try {
      const response = await apiClient.post(`/projects/${projectId}/writing/analyze-text`, { text });

      if (!response.success) {
        throw new Error(response.message || '文本分析失败');
      }

      // 暂时不处理分析结果
    } catch (error: any) {
      // 不显示错误，静默失败
    }
  };

  // 保存内容
  const saveContent = async (content: string, saveType: 'temp' | 'final', overrideChapterId?: number): Promise<boolean> => {
    const currentChapterId = overrideChapterId || selectedChapter?.template_chapter_id;
    if (!currentChapterId) return false;

    try {
      const requestBody = {
        content,
        save_type: saveType
      };

      const response = await apiClient.put(`/projects/${projectId}/writing/chapter/${currentChapterId}`, requestBody);

      if (!response.success) {
        throw new Error(response.message || '保存失败');
      }

      showSnackbar(response.message || '保存成功', 'success');
      
      // 如果是最终保存，重新加载项目结构以更新状态
      if (saveType === 'final') {
        await loadProjectStructure(false); // 刷新结构但不自动跳转到第一个章节
        // 使用最新的projectStructure来查找章节
        const structureResponse = await apiClient.get(`/projects/${projectId}/writing/structure`);
        if (structureResponse.success) {
          const chapter = findChapterById(structureResponse.data.chapters, currentChapterId);
          if (chapter) {
            setSelectedChapter(chapter);
            await loadChapterContent(chapter);
          }
        }
      }
      
      return true;
    } catch (error: any) {
      showSnackbar('保存失败', 'error');
      return false;
    }
  };

  // 递归查找章节
  const findChapterById = (chapters: Chapter[], chapterId: number): Chapter | null => {
    for (const chapter of chapters) {
      if (chapter.template_chapter_id === chapterId) {
        return chapter;
      }
      if (chapter.children && chapter.children.length > 0) {
        const found = findChapterById(chapter.children, chapterId);
        if (found) return found;
      }
    }
    return null;
  };

  // 生成初稿
  const generateDraft = async (userPrompt = ''): Promise<string | null> => {
    if (!selectedChapter) return null;

    try {
      const startTime = Date.now();
      
      const response = await apiClient.post(`/projects/${projectId}/writing/generate-draft`, {
        chapterId: selectedChapter.template_chapter_id,
        user_prompt: userPrompt
      }, {
        timeout: 180000 // 3分钟超时，给LLM更多时间
      });

      const endTime = Date.now();

      if (!response.success) {
        throw new Error(response.message || '生成初稿失败');
      }

      return response.data.generated_content;
    } catch (error: any) {
      if (error.code === 'ECONNABORTED') {
        showSnackbar('请求超时，请稍后重试', 'error');
      } else {
        showSnackbar('生成初稿失败', 'error');
      }
      return null;
    }
  };

  // 处理章节选择
  const handleChapterSelect = async (chapter: Chapter) => {
    setSelectedChapter(chapter);
    setChapterContent(''); // Clear previous content immediately

    setLoading(true); // Always show loading state on chapter change

    try {
      if (chapter.is_indicator_driven) {
        // 调用指标章节渲染API
        const response = await apiClient.post(`/projects/${projectId}/writing/render-indicator-chapter`, {
          chapterId: chapter.template_chapter_id
        });
        
        if (!response.success) {
          throw new Error(response.message || '渲染指标章节失败');
        }
        
        setChapterContent(response.data.content);
        
        // 指标驱动章节：渲染完成后立即自动保存为最终版
        try {
          await saveContent(response.data.content, 'final', chapter.template_chapter_id);
        } catch (_) {
          // 自动保存失败不阻塞展示
        }
      } else {
        // 加载QA驱动章节内容
        await loadChapterContent(chapter);
      }
    } catch (error: any) {
      showSnackbar(error.message || '加载章节内容失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 处理内容变化
  const handleContentChange = (content: string) => {
    setChapterContent(content);
    
    // 防抖处理文本分析
    clearTimeout((window as any).analysisTimeout);
    (window as any).analysisTimeout = setTimeout(() => {
      analyzeText(content);
    }, 1000);
  };

  // 显示提示信息
  const showSnackbar = (message: string, severity: 'info' | 'success' | 'warning' | 'error' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // 关闭提示信息
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 全局预览
  const handleGlobalPreview = () => {
    window.open(`/api/v1/projects/${projectId}/writing/preview`, '_blank');
  };

  // 刷新项目结构
  const handleRefresh = () => {
    loadProjectStructure();
  };

  useEffect(() => {
    if (projectId) {
      loadProjectStructure();
    }
  }, [projectId, loadProjectStructure]);

  if (loading && !projectStructure) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          width: '100vw',
          bgcolor: 'background.default'
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>加载中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          width: '100vw',
          bgcolor: 'background.default'
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100vh',
      width: '100vw',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: 'background.default'
    }}>
      {/* 顶部工具栏 */}
      <AppBar 
        position="static" 
        elevation={1}
        sx={{ 
          bgcolor: 'background.paper', 
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 48 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, fontSize: '1rem' }}>
            {projectStructure?.project?.name} - 交互式写作
          </Typography>
          <Tooltip title="全局预览">
            <IconButton onClick={handleGlobalPreview} size="small">
              <PreviewIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="刷新">
            <IconButton onClick={handleRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* 主要内容区域 - 两栏布局 */}
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
        height: 'calc(100vh - 48px)'
      }}>
        {/* 左侧章节树 - 30% */}
        <Box sx={{ 
          width: '30%',
          minWidth: '300px',
          maxWidth: '400px',
          bgcolor: 'background.paper',
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            p: 2, 
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.default'
          }}>
            <Typography variant="subtitle1" fontWeight="bold">
              章节目录
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <ChapterTree
              chapters={projectStructure?.chapters || []}
              selectedChapter={selectedChapter}
              onChapterSelect={handleChapterSelect}
            />
          </Box>
        </Box>

        {/* 右侧写作区域 - 70% */}
        <Box sx={{ 
          width: '70%',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%' 
            }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>加载章节内容...</Typography>
            </Box>
          ) : (
            <WritingArea
              selectedChapter={selectedChapter}
              content={chapterContent}
              onContentChange={handleContentChange}
              onSave={saveContent}
              onGenerateDraft={generateDraft}
              projectId={projectId}
            />
          )}
        </Box>
      </Box>

      {/* 提示信息 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WritingWorkspace;
